import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  addEdge,
  applyEdgeChanges,
  applyNodeChanges,
  Background,
  Controls,
  Handle,
  MiniMap,
  Position,
  ReactFlow,
  ReactFlowProvider,
  useReactFlow,
  type Connection,
  type Edge,
  type EdgeChange,
  type Node,
  type NodeChange,
  type NodeMouseHandler,
  type EdgeMouseHandler,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { Check, Copy, RefreshCw, Trash2 } from "lucide-react";

import {
  flowNodes,
  flowEdges,
  aiTradingFlowNodes,
  aiTradingFlowEdges,
} from "../StrategiesDemoPage/demoData";
import type { FlowNode, FlowEdge } from "../StrategiesDemoPage/types";

// ─── Stage → max step mapping ────────────────────────────────────────────────

const STAGE_STEP: Record<string, number> = {
  baseline: 1,
  "1": 1,
  "2": 2,
  "3": 3,
  enhanced: 4,
};

const STAGES = ["baseline", "1", "2", "3", "enhanced"] as const;
type Stage = (typeof STAGES)[number];
type Strategy = "dual_ma" | "ai_strategy";

type ContextMenu = {
  x: number;
  y: number;
  kind: "node" | "edge";
  id: string;
};

// ─── Data adapter: FlowNode[] → ReactFlow Node[] ─────────────────────────────

function adaptToReactFlow(
  sourceNodes: FlowNode[],
  sourceEdges: FlowEdge[],
  maxStep: number,
  preservedPositions: Map<string, { x: number; y: number }>
): { nodes: Node[]; edges: Edge[] } {
  const visibleNodes = sourceNodes.filter((n) => n.step <= maxStep);
  const visibleEdges = sourceEdges.filter((e) => e.step <= maxStep);

  const nodes: Node[] = visibleNodes.map((n) => ({
    id: n.id,
    type: "debugDemo",
    position: preservedPositions.get(n.id) ?? { x: n.x, y: n.y },
    data: {
      label: n.label,
      sub: n.sub,
      icon: n.icon,
      color: n.color,
      borderColor: n.borderColor,
      bg: n.bg,
      step: n.step,
    },
    draggable: true,
    selectable: true,
  }));

  const edges: Edge[] = visibleEdges.map((e) => ({
    id: e.id,
    source: e.from,
    target: e.to,
    label: e.label || undefined,
    sourceHandle: "out",
    targetHandle: "in",
    style: { stroke: "#4ade80", strokeWidth: 2 },
  }));

  return { nodes, edges };
}

// ─── Custom node ─────────────────────────────────────────────────────────────

function DebugDemoNode({ data }: { data: any }) {
  const Icon = data.icon;
  return (
    <div className="flex flex-col items-center" style={{ width: 130 }}>
      <div
        className={`w-16 h-16 rounded-2xl border-2 flex items-center justify-center relative shadow-md ${data.borderColor} ${data.bg}`}
      >
        <Handle
          id="in"
          type="target"
          position={Position.Left}
          style={{
            width: 10,
            height: 10,
            background: "#94a3b8",
            border: "2px solid #fff",
            left: -6,
          }}
        />
        <Handle
          id="out"
          type="source"
          position={Position.Right}
          style={{
            width: 10,
            height: 10,
            background: "#94a3b8",
            border: "2px solid #fff",
            right: -6,
          }}
        />
        {Icon && <Icon size={24} strokeWidth={2} className={data.color} />}
        {/* Step badge */}
        <div
          className="absolute -top-2 -right-2 text-[9px] font-bold px-1.5 py-0.5 rounded-full"
          style={{
            background: "var(--accent-blue, #2f81f7)",
            color: "#fff",
            lineHeight: 1.4,
          }}
        >
          s{data.step}
        </div>
      </div>
      <div className="mt-2 text-center px-1">
        <div
          className="text-[12px] font-semibold"
          style={{ color: "var(--text-primary, #e6edf3)" }}
        >
          {data.label}
        </div>
        <div
          className="text-[10px] mt-0.5"
          style={{ color: "var(--text-secondary, #8b949e)" }}
        >
          {data.sub}
        </div>
      </div>
    </div>
  );
}

const NODE_TYPES = { debugDemo: DebugDemoNode };

// ─── Export JSON builder ──────────────────────────────────────────────────────

function buildExportJson(
  strategy: Strategy,
  stage: Stage,
  nodes: Node[],
  edges: Edge[]
): string {
  const out = {
    strategy,
    stage,
    nodes: nodes.map((n) => ({
      id: n.id,
      label: (n.data as any).label,
      sub: (n.data as any).sub,
      x: Math.round(n.position.x),
      y: Math.round(n.position.y),
    })),
    edges: edges.map((e) => ({
      id: e.id,
      from: e.source,
      to: e.target,
      label: typeof e.label === "string" ? e.label : "",
    })),
  };
  return JSON.stringify(out, null, 2);
}

// ─── Inner canvas (needs useReactFlow) ───────────────────────────────────────

function FlowDebugCanvas({
  strategy,
  stage,
  userPositionsRef,
  onReset,
}: {
  strategy: Strategy;
  stage: Stage;
  userPositionsRef: React.MutableRefObject<Map<string, { x: number; y: number }>>;
  onReset: () => void;
}) {
  const { fitView } = useReactFlow();

  const sourceNodes: FlowNode[] =
    strategy === "dual_ma" ? flowNodes : aiTradingFlowNodes;
  const sourceEdges: FlowEdge[] =
    strategy === "dual_ma" ? flowEdges : aiTradingFlowEdges;
  const maxStep = STAGE_STEP[stage];

  const [nodes, setNodes] = useState<Node[]>([]);
  const [edges, setEdges] = useState<Edge[]>([]);
  const [copied, setCopied] = useState(false);
  const [contextMenu, setContextMenu] = useState<ContextMenu | null>(null);

  // Derive graph when strategy/stage changes
  useEffect(() => {
    const { nodes: n, edges: e } = adaptToReactFlow(
      sourceNodes,
      sourceEdges,
      maxStep,
      userPositionsRef.current
    );
    setNodes(n);
    setEdges(e);
    setContextMenu(null);
    requestAnimationFrame(() => fitView({ padding: 0.25, duration: 600 }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [strategy, stage]);

  const onNodesChange = useCallback(
    (changes: NodeChange[]) => {
      for (const change of changes) {
        if (change.type === "position" && change.position) {
          userPositionsRef.current.set(change.id, change.position);
        }
      }
      setNodes((nds) => applyNodeChanges(changes, nds));
    },
    [userPositionsRef]
  );

  const onEdgesChange = useCallback((changes: EdgeChange[]) => {
    setEdges((eds) => applyEdgeChanges(changes, eds));
  }, []);

  const onConnect = useCallback((params: Connection) => {
    setEdges((eds) =>
      addEdge({ ...params, style: { stroke: "#4ade80", strokeWidth: 2 } }, eds)
    );
  }, []);

  // ── Delete helpers ──────────────────────────────────────────────────────────
  const deleteNodeById = useCallback((id: string) => {
    setNodes((nds) => nds.filter((n) => n.id !== id));
    setEdges((eds) => eds.filter((e) => e.source !== id && e.target !== id));
  }, []);

  const deleteEdgeById = useCallback((id: string) => {
    setEdges((eds) => eds.filter((e) => e.id !== id));
  }, []);

  const deleteSelected = useCallback(() => {
    setNodes((nds) => {
      const ids = new Set(nds.filter((n) => n.selected).map((n) => n.id));
      if (ids.size === 0) return nds;
      setEdges((eds) =>
        eds.filter((e) => !ids.has(e.source) && !ids.has(e.target))
      );
      return nds.filter((n) => !ids.has(n.id));
    });
    setEdges((eds) => eds.filter((e) => !e.selected));
  }, []);

  // ── Keyboard delete ─────────────────────────────────────────────────────────
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (e.key !== "Backspace" && e.key !== "Delete") return;
      const target = e.target as HTMLElement;
      const tag = target?.tagName?.toLowerCase();
      if (tag === "input" || tag === "textarea" || target?.isContentEditable)
        return;
      e.preventDefault();
      deleteSelected();
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [deleteSelected]);

  // ── Context menu ────────────────────────────────────────────────────────────
  const openContextMenu = useCallback(
    (e: React.MouseEvent, kind: "node" | "edge", id: string) => {
      e.preventDefault();
      e.stopPropagation();

      const MENU_W = 180;
      const MENU_H = 44;
      const pad = 8;
      const x = Math.min(
        Math.max(pad, e.clientX),
        window.innerWidth - MENU_W - pad
      );
      const y = Math.min(
        Math.max(pad, e.clientY),
        window.innerHeight - MENU_H - pad
      );
      setContextMenu({ x, y, kind, id });
    },
    []
  );

  const onNodeContextMenu: NodeMouseHandler = useCallback(
    (e, node) => openContextMenu(e as unknown as React.MouseEvent, "node", node.id),
    [openContextMenu]
  );

  const onEdgeContextMenu: EdgeMouseHandler = useCallback(
    (e, edge) => openContextMenu(e as unknown as React.MouseEvent, "edge", edge.id),
    [openContextMenu]
  );

  // ── Reset layout ────────────────────────────────────────────────────────────
  const handleReset = useCallback(() => {
    userPositionsRef.current.clear();
    const { nodes: n, edges: e } = adaptToReactFlow(
      sourceNodes,
      sourceEdges,
      maxStep,
      new Map()
    );
    setNodes(n);
    setEdges(e);
    setContextMenu(null);
    requestAnimationFrame(() => fitView({ padding: 0.25, duration: 600 }));
    onReset();
  }, [sourceNodes, sourceEdges, maxStep, userPositionsRef, fitView, onReset]);

  // ── Export ──────────────────────────────────────────────────────────────────
  const exportJson = useMemo(
    () => buildExportJson(strategy, stage, nodes, edges),
    [strategy, stage, nodes, edges]
  );

  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(exportJson).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, [exportJson]);

  return (
    <div
      style={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}
    >
      {/* Canvas */}
      <div style={{ flex: 1, minHeight: 0, position: "relative" }}>
        {/* Reset button */}
        <button
          onClick={handleReset}
          className="absolute top-3 right-3 z-10 flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg"
          style={{
            background: "var(--bg-panel, #161b22)",
            border: "1px solid var(--border-subtle, #30363d)",
            color: "var(--text-secondary, #8b949e)",
          }}
        >
          <RefreshCw size={12} />
          Reset Layout
        </button>

        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          onNodeContextMenu={onNodeContextMenu}
          onEdgeContextMenu={onEdgeContextMenu}
          onPaneClick={() => setContextMenu(null)}
          nodeTypes={NODE_TYPES}
          nodesDraggable
          nodesConnectable
          elementsSelectable
          fitView
          minZoom={0.1}
          maxZoom={3}
          proOptions={{ hideAttribution: true }}
          deleteKeyCode={null}
          defaultEdgeOptions={{ style: { stroke: "#4ade80", strokeWidth: 2 } }}
          connectionLineStyle={{ stroke: "#2f81f7", strokeWidth: 2 }}
          style={{ background: "var(--bg-app, #0d1117)" }}
        >
          <Background color="#30363d" gap={24} size={1.5} />
          <Controls
            style={{
              background: "var(--bg-panel, #161b22)",
              border: "1px solid var(--border-subtle, #30363d)",
            }}
          />
          <MiniMap
            pannable
            zoomable
            style={{
              background: "var(--bg-panel, #161b22)",
              border: "1px solid var(--border-subtle, #30363d)",
            }}
            nodeColor={() => "#2f81f7"}
            maskColor="rgba(13, 17, 23, 0.6)"
          />
        </ReactFlow>

        {/* Context menu */}
        {contextMenu && (
          <div
            role="presentation"
            onMouseDown={() => setContextMenu(null)}
            style={{ position: "fixed", inset: 0, zIndex: 50 }}
          >
            <div
              role="menu"
              onMouseDown={(e) => e.stopPropagation()}
              style={{
                position: "fixed",
                left: contextMenu.x,
                top: contextMenu.y,
                width: 180,
                borderRadius: 10,
                border: "1px solid var(--border-subtle, #30363d)",
                background: "var(--bg-panel, #161b22)",
                boxShadow: "0 16px 48px rgba(0,0,0,0.6)",
                overflow: "hidden",
                zIndex: 51,
              }}
            >
              <button
                role="menuitem"
                className="w-full flex items-center gap-2.5 px-3 py-2.5 text-sm"
                style={{
                  color: "#f85149",
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                  textAlign: "left",
                  transition: "background 0.1s",
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLElement).style.background =
                    "rgba(248,81,73,0.12)";
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLElement).style.background =
                    "transparent";
                }}
                onClick={() => {
                  if (contextMenu.kind === "node") deleteNodeById(contextMenu.id);
                  if (contextMenu.kind === "edge") deleteEdgeById(contextMenu.id);
                  setContextMenu(null);
                }}
              >
                <Trash2 size={14} />
                <span>Delete {contextMenu.kind}</span>
                <span
                  style={{
                    marginLeft: "auto",
                    fontSize: 10,
                    color: "var(--text-secondary, #8b949e)",
                  }}
                >
                  Del
                </span>
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Export panel */}
      <div
        style={{
          borderTop: "1px solid var(--border-subtle, #30363d)",
          background: "var(--bg-panel, #161b22)",
          padding: "12px 16px",
          flexShrink: 0,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 8,
          }}
        >
          <span
            style={{
              fontSize: 12,
              fontWeight: 600,
              color: "var(--text-secondary, #8b949e)",
              letterSpacing: "0.05em",
              textTransform: "uppercase",
            }}
          >
            Export JSON
          </span>
          <button
            onClick={handleCopy}
            className="flex items-center gap-1.5 px-3 py-1 text-xs font-medium rounded-lg"
            style={{
              background: copied
                ? "rgba(46,160,67,0.15)"
                : "rgba(47,129,247,0.15)",
              color: copied ? "#3fb950" : "#2f81f7",
              border: copied
                ? "1px solid rgba(46,160,67,0.3)"
                : "1px solid rgba(47,129,247,0.3)",
            }}
          >
            {copied ? <Check size={12} /> : <Copy size={12} />}
            {copied ? "Copied!" : "Copy"}
          </button>
        </div>
        <textarea
          readOnly
          value={exportJson}
          style={{
            width: "100%",
            maxHeight: 200,
            minHeight: 80,
            resize: "vertical",
            background: "var(--bg-app, #0d1117)",
            color: "#e6edf3",
            border: "1px solid var(--border-subtle, #30363d)",
            borderRadius: 8,
            padding: "10px 12px",
            fontFamily:
              "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
            fontSize: 11,
            lineHeight: 1.6,
            outline: "none",
          }}
        />
      </div>
    </div>
  );
}

// ─── Page root ────────────────────────────────────────────────────────────────

export default function FlowDebugPage() {
  const [strategy, setStrategy] = useState<Strategy>("dual_ma");
  const [stage, setStage] = useState<Stage>("baseline");
  const userPositionsRef = useRef<Map<string, { x: number; y: number }>>(
    new Map()
  );
  const [resetKey, setResetKey] = useState(0);

  const handleStrategyChange = (s: Strategy) => {
    if (s === strategy) return;
    userPositionsRef.current.clear();
    setStrategy(s);
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        background: "var(--bg-app, #0d1117)",
        color: "var(--text-primary, #e6edf3)",
        fontFamily: "system-ui, sans-serif",
      }}
    >
      {/* Warning banner */}
      <div
        style={{
          background: "rgba(210,153,34,0.12)",
          borderBottom: "1px solid rgba(210,153,34,0.25)",
          padding: "6px 16px",
          fontSize: 12,
          color: "#e3b341",
          display: "flex",
          alignItems: "center",
          gap: 6,
          flexShrink: 0,
        }}
      >
        <span>⚠️</span>
        <span>
          Developer Debug Page — Not linked in navigation. Access via URL:{" "}
          <code style={{ fontFamily: "monospace" }}>/flow-debug</code>
        </span>
      </div>

      {/* Header */}
      <div
        style={{
          height: 52,
          borderBottom: "1px solid var(--border-subtle, #30363d)",
          background: "var(--bg-panel, #161b22)",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "0 16px",
          flexShrink: 0,
          gap: 16,
        }}
      >
        {/* Title */}
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontSize: 16, fontWeight: 700 }}>
            🔧 Flow Debug Studio
          </span>
          <span
            style={{
              fontSize: 10,
              padding: "2px 8px",
              borderRadius: 999,
              background: "rgba(47,129,247,0.15)",
              color: "#2f81f7",
              border: "1px solid rgba(47,129,247,0.3)",
              fontWeight: 600,
              letterSpacing: "0.05em",
            }}
          >
            DEV ONLY
          </span>
        </div>

        {/* Controls */}
        <div style={{ display: "flex", alignItems: "center", gap: 20 }}>
          {/* Strategy selector */}
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span
              style={{
                fontSize: 12,
                color: "var(--text-secondary, #8b949e)",
                fontWeight: 500,
              }}
            >
              Strategy
            </span>
            <div style={{ display: "flex", gap: 4 }}>
              {(["dual_ma", "ai_strategy"] as Strategy[]).map((s) => (
                <button
                  key={s}
                  onClick={() => handleStrategyChange(s)}
                  style={{
                    padding: "4px 12px",
                    borderRadius: 6,
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: "pointer",
                    transition: "all 0.15s",
                    background:
                      strategy === s ? "rgba(47,129,247,0.2)" : "transparent",
                    border:
                      strategy === s
                        ? "1px solid rgba(47,129,247,0.5)"
                        : "1px solid var(--border-subtle, #30363d)",
                    color:
                      strategy === s
                        ? "#2f81f7"
                        : "var(--text-secondary, #8b949e)",
                  }}
                >
                  {s === "dual_ma" ? "双均线" : "AI Strategy"}
                </button>
              ))}
            </div>
          </div>

          {/* Divider */}
          <div
            style={{
              width: 1,
              height: 24,
              background: "var(--border-subtle, #30363d)",
            }}
          />

          {/* Stage selector */}
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span
              style={{
                fontSize: 12,
                color: "var(--text-secondary, #8b949e)",
                fontWeight: 500,
              }}
            >
              Stage
            </span>
            <div style={{ display: "flex", gap: 4 }}>
              {STAGES.map((s) => (
                <button
                  key={s}
                  onClick={() => setStage(s)}
                  style={{
                    padding: "4px 10px",
                    borderRadius: 6,
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: "pointer",
                    transition: "all 0.15s",
                    background:
                      stage === s ? "rgba(163,113,247,0.2)" : "transparent",
                    border:
                      stage === s
                        ? "1px solid rgba(163,113,247,0.5)"
                        : "1px solid var(--border-subtle, #30363d)",
                    color:
                      stage === s
                        ? "#a371f7"
                        : "var(--text-secondary, #8b949e)",
                  }}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Step hint */}
        <div
          style={{ fontSize: 11, color: "var(--text-secondary, #8b949e)" }}
        >
          step ≤ {STAGE_STEP[stage]}
        </div>
      </div>

      {/* Canvas area */}
      <div style={{ flex: 1, minHeight: 0 }}>
        <ReactFlowProvider>
          <FlowDebugCanvas
            key={`${strategy}-${resetKey}`}
            strategy={strategy}
            stage={stage}
            userPositionsRef={userPositionsRef}
            onReset={() => setResetKey((k) => k + 1)}
          />
        </ReactFlowProvider>
      </div>
    </div>
  );
}
